import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest } from "@/utils/models";
import { isRequestInstanceOfReplaceShiftRequest } from "@/utils/utilFunctions";

type useGetApprovalTitleProps = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	neededNursesAmount: number;
};

export const useGetApprovalTitle = ({ request, neededNursesAmount }: useGetApprovalTitleProps) => {
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const { t } = useTranslation();

	return useMemo(() => {
		if (request?.request_type === NotificationType.ReplaceAgency) {
			return t("replace.agency.modal.title", {
				name: getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id),
			});
		}

		let trailingText = "";
		if (request && request.request_type === NotificationType.ReplaceShift && isRequestInstanceOfReplaceShiftRequest(request)) {
			trailingText = `Replace ${getNurseFormattedName(request.replacement_nurse_id)}`;
		} else if (request && request.request_type === NotificationType.Swap) {
			trailingText = "Swap";
		}

		const displayBonus = request.current_bonus ?? request.bonus;
		return `Request Approvals ${trailingText ? `| ${trailingText}` : `(${neededNursesAmount} needed)`} ${displayBonus ? ` (+$${displayBonus})` : ""}`;
	}, [neededNursesAmount, request, getNurseFormattedName, t]);
};
